import './LoginForm.css'

import {useCallback, useMemo, useRef, useState} from 'react'
import {type ComAtprotoServerDescribeServer} from '@atproto/api'
import {XRPCError} from '@atproto/xrpc'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {useRequestNotificationsPermission} from '#/lib/notifications/notifications'
import {ROUTES} from '#/lib/routes/constants.ts'
import {cleanError} from '#/lib/strings/errors'
import {logger} from '#/logger'
import {useSetHasCheckedForStarterPack} from '#/state/preferences/used-starter-packs'
import {useAgent, useSessionApi} from '#/state/session'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {FormError} from '#/components/forms/FormError'
import LockIcon from '#/components/icons/LockIcon'
import MailIcon from '#/components/icons/MailIcon'
import {Loader} from '#/components/Loader'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {TOKENS} from '#/wsocial/service/design-system/tokens'
import {extractQrTextFromImageUrl} from '#/wsocial/utils/qrCode'
import logoSvg from '../../../assets/icon-wsocial.svg'
import widentityLogoSvg from '../../../assets/images/onboarding/widentity_logo.svg'
import {useQrSession} from '../SignUpWizard/useQrSession'

type ServiceDescription = ComAtprotoServerDescribeServer.OutputSchema

const T = {
  inputBg: '#272F44',
  textMedium: '#D0CFD7',
  textPlaceholder: '#9997A7',
  accentHover: TOKENS.dark.custom_accent_hover,
} as const

export const LoginFormMobile = ({
  error,
  serviceUrl,
  setError,
  onPressForgotPassword,
  onAttemptSuccess,
  onAttemptFailed,
}: {
  error: string
  serviceUrl: string
  serviceDescription: ServiceDescription | undefined
  initialHandle: string
  setError: (v: string) => void
  setServiceUrl: (v: string) => void
  onPressRetryConnect: () => void
  onPressBack: () => void
  onPressForgotPassword: () => void
  onAttemptSuccess: () => void
  onAttemptFailed: () => void
}) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [authFactorToken, setAuthFactorToken] = useState('')
  const [isAuthFactorTokenNeeded, setIsAuthFactorTokenNeeded] = useState(false)
  const [signTagUrl, setSignTagUrl] = useState('')
  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const authFactorRef = useRef<HTMLInputElement>(null)
  const {_} = useLingui()
  const {login, resumeSession} = useSessionApi()
  const requestNotificationsPermission = useRequestNotificationsPermission()
  const {setShowLoggedOut} = useLoggedOutViewControls()
  const setHasCheckedForStarterPack = useSetHasCheckedForStarterPack()
  const agent = useAgent()
  const ss = useSemanticTokens()

  const {
    sessionStatus,
    isInitLoading,
    isInitError,
    initErrorMessage,
    isStatusError,
    statusErrorMessage,
    refetchInit,
  } = useQrSession({
    queryKey: 'quick-login',
    initFn: async (signal, prevServiceId, prevSessionId) => {
      const data = await agent.quicklogin.init(
        {
          allowCreate: true,
          ...(prevServiceId && {serviceId: prevServiceId}),
          ...(prevSessionId && {sessionId: prevSessionId}),
        },
        {signal},
      )
      if (data.qrCodeUrl) {
        try {
          const url = await extractQrTextFromImageUrl(data.qrCodeUrl)
          if (url) setSignTagUrl(url)
        } catch (e) {
          logger.error('Error extracting QR code url from image')
        }
      }
      return data
    },
    statusFn: (params, signal) => agent.quicklogin.status(params, {signal}),
    autoRefresh: true,
    onCompleted: data => {
      if (data.result) {
        void (async () => {
          try {
            const switched = await resumeSession({
              service: serviceUrl,
              ...data.result,
              active: true,
            })
            if (!switched) {
              throw new Error('Session switch was superseded')
            }
            onAttemptSuccess()
            setShowLoggedOut(false)
            setHasCheckedForStarterPack(true)
            void requestNotificationsPermission('Login')
          } catch (e) {
            logger.error('Quick Login session resume failed', {error: e})
            setError(_(msg`Login failed. Please try again.`))
          }
        })()
      }
    },
  })

  const onSigninWithWIdentity = useCallback(() => {
    window.location.href = signTagUrl
  }, [signTagUrl])

  const onSubmit = useCallback(async () => {
    // Fall back to DOM values to handle browser autofill (Chrome delays
    // applying autofill values to state until after a user gesture)
    const emailValue = email || emailRef.current?.value || ''
    const passwordValue = password || passwordRef.current?.value || ''
    if (isProcessing || !emailValue || !passwordValue) return
    setError('')
    setIsProcessing(true)
    try {
      await login(
        {
          service: serviceUrl,
          identifier: emailValue,
          password: passwordValue,
          authFactorToken: authFactorToken.trim() || undefined,
        },
        'LoginForm',
      )
      onAttemptSuccess()
      setShowLoggedOut(false)
      setHasCheckedForStarterPack(true)
      void requestNotificationsPermission('Login')
    } catch (err: unknown) {
      if (err instanceof XRPCError && err.error === 'AuthFactorTokenRequired') {
        setIsAuthFactorTokenNeeded(true)
        setError('')
      } else {
        onAttemptFailed()
        setError(cleanError(err))
      }
    } finally {
      setIsProcessing(false)
    }
  }, [
    isProcessing,
    email,
    password,
    authFactorToken,
    login,
    serviceUrl,
    onAttemptSuccess,
    onAttemptFailed,
    setError,
    setShowLoggedOut,
    setHasCheckedForStarterPack,
    requestNotificationsPermission,
  ])

  const inputStyle: React.CSSProperties = {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    fontSize: 15,
    fontWeight: 400,
    color: '#fff',
    lineHeight: '23px',
    minWidth: 0,
  }

  const inputRowStyle: React.CSSProperties = {
    backgroundColor: T.inputBg,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    height: 44,
    padding: '0 12px',
    borderRadius: 10,
  }

  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '17px',
    color: T.textMedium,
    display: 'block',
    margin: 0,
  }

  const canSubmit = useMemo(
    () =>
      !isProcessing && (!isAuthFactorTokenNeeded || !!authFactorToken.trim()),
    [authFactorToken, isAuthFactorTokenNeeded, isProcessing],
  )

  return (
    <div
      style={{
        backgroundColor: ss.bg.default,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        padding: '0 16px',
      }}>
      {/* Logo */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 0 32px',
        }}>
        <img src={logoSvg} alt="W Social" style={{width: 80, height: 80}} />
      </div>

      {/* Heading */}
      <h1
        style={{
          fontSize: 30,
          fontWeight: 700,
          lineHeight: '35px',
          color: '#fff',
          margin: '0 0 8px',
        }}>
        <Trans>Sign in</Trans>
      </h1>
      <p
        style={{
          fontSize: 13,
          fontWeight: 400,
          lineHeight: '17px',
          color: T.textMedium,
          margin: '0 0 24px',
        }}>
        <Trans>
          Authenticate using W Identity or sign in with email and password.
        </Trans>
      </p>

      {/* W Identity card */}
      <div
        style={{
          border: `1px solid ${ss.border.contrast_low}`,
          borderRadius: 8,
          padding: '20px 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          marginBottom: 16,
        }}>
        <img
          src={widentityLogoSvg}
          alt="W Identity"
          style={{width: 64, height: 64, borderRadius: 14}}
        />
        <p
          style={{
            color: '#fff',
            fontSize: 17,
            fontWeight: 600,
            lineHeight: '22px',
            margin: 0,
          }}>
          W Identity
        </p>
        {isInitLoading && <Loader fill={ss.menu.icon.color} size="sm" />}
        {isInitError && (
          <>
            <p
              style={{
                color: ss.admonition.errorColor,
                fontSize: 13,
                margin: 0,
                textAlign: 'center',
              }}>
              {initErrorMessage}
            </p>
            <button
              onClick={refetchInit}
              className="LinkNew"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 15,
                padding: 0,
              }}>
              <Trans>Retry</Trans>
            </button>
          </>
        )}
        {!isInitLoading && !isInitError && (
          <>
            {sessionStatus === 'failed' || isStatusError ? (
              <>
                <p
                  style={{
                    color: ss.admonition.errorColor,
                    fontSize: 13,
                    margin: 0,
                    textAlign: 'center',
                  }}>
                  {statusErrorMessage}
                </p>
                <button
                  onClick={refetchInit}
                  className="LinkNew"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 15,
                    padding: 0,
                  }}>
                  <Trans>Retry</Trans>
                </button>
              </>
            ) : (
              <a
                href={signTagUrl || undefined}
                onClick={onSigninWithWIdentity}
                style={{
                  display: 'inline-block',
                  color: ss.link.text,
                  fontSize: 15,
                  fontWeight: 500,
                  lineHeight: '20px',
                  padding: '10px 20px',
                  borderRadius: 8,
                  textDecoration: 'none',
                  opacity: signTagUrl ? 1 : 0.5,
                  pointerEvents: signTagUrl ? 'auto' : 'none',
                }}>
                {sessionStatus === 'completed' ? (
                  <Trans>Authenticated!</Trans>
                ) : (
                  /*sessionStatus === 'pending' ? (
                    <Trans>Waiting...</Trans>
                  ) : */ <Trans>Authenticate</Trans>
                )}
              </a>
            )}
          </>
        )}
      </div>

      {/* "or" divider */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
        }}>
        <div
          style={{
            flex: 1,
            height: 1,
            backgroundColor: ss.border.contrast_low,
          }}
        />
        <span style={{color: T.textMedium, fontSize: 13, fontWeight: 400}}>
          or
        </span>
        <div
          style={{
            flex: 1,
            height: 1,
            backgroundColor: ss.border.contrast_low,
          }}
        />
      </div>

      {/* Email */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginBottom: 16,
        }}>
        <label style={labelStyle}>
          <Trans>Email</Trans>
        </label>
        <div style={inputRowStyle}>
          <MailIcon />
          <input
            ref={emailRef}
            type="email"
            placeholder={_(msg`Your email`)}
            value={email}
            onChange={e => setEmail(e.target.value)}
            onAnimationStart={e => {
              if (e.animationName === 'onAutoFillStart')
                setEmail(e.currentTarget.value)
            }}
            disabled={isProcessing}
            autoComplete="email"
            className="SignInInput"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Password */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginBottom: 16,
        }}>
        <label style={labelStyle}>
          <Trans>Password</Trans>
        </label>
        <div style={inputRowStyle}>
          <LockIcon />
          <input
            ref={passwordRef}
            type="password"
            placeholder={_(msg`Your password`)}
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') void onSubmit()
            }}
            onAnimationStart={e => {
              if (e.animationName === 'onAutoFillStart')
                setPassword(e.currentTarget.value)
            }}
            disabled={isProcessing}
            autoComplete="current-password"
            className="SignInInput"
            style={inputStyle}
          />
        </div>
      </div>

      {isAuthFactorTokenNeeded && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            marginBottom: 16,
          }}>
          <label style={labelStyle}>
            <Trans>2FA confirmation</Trans>
          </label>
          <p
            style={{
              fontSize: 13,
              color: T.textMedium,
              margin: 0,
              lineHeight: '17px',
            }}>
            <Trans>A sign in code has been sent to your email address.</Trans>
          </p>
          <div style={inputRowStyle}>
            <LockIcon />
            <input
              ref={authFactorRef}
              type="text"
              autoComplete="one-time-code"
              placeholder={_(msg`Confirmation code`)}
              value={authFactorToken}
              onChange={e => setAuthFactorToken(e.target.value.toUpperCase())}
              onKeyDown={e => {
                if (e.key === 'Enter') void onSubmit()
              }}
              disabled={isProcessing}
              autoFocus
              className="SignInInput"
              style={{
                ...inputStyle,
                textTransform: authFactorToken ? 'uppercase' : 'none',
              }}
            />
          </div>
        </div>
      )}

      <FormError error={error} />

      {/* Full-width sign-in button */}
      <button
        onClick={() => void onSubmit()}
        disabled={!canSubmit}
        className="MobileSignInBtn"
        style={{
          width: '100%',
          backgroundColor: ss.button.primary.bg,
          color: ss.button.primary.text,
          fontSize: 17,
          fontWeight: 600,
          height: 56,
          borderRadius: 12,
          border: 'none',
          cursor: canSubmit ? 'pointer' : 'not-allowed',
          opacity: canSubmit ? 1 : 0.6,
          marginTop: 8,
          marginBottom: 16,
        }}>
        {isProcessing ? '...' : <Trans>Sign in</Trans>}
      </button>

      {/* Forgot password */}
      <button
        onClick={onPressForgotPassword}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 400,
          color: T.textMedium,
          padding: '8px 0',
          textAlign: 'center',
          width: '100%',
        }}>
        <Trans>Forgot password?</Trans>
      </button>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          marginTop: 8,
          paddingBottom: 32,
        }}>
        <p
          style={{
            fontSize: 12,
            color: T.textMedium,
            margin: 0,
            textAlign: 'center',
          }}>
          <Trans>Having trouble?</Trans>{' '}
          <a href="mailto:support@wsocial.eu" className="LinkNew">
            <Trans>Contact support</Trans>
          </a>
        </p>
        <p
          style={{
            fontSize: 12,
            color: T.textMedium,
            margin: 0,
            textAlign: 'center',
          }}>
          <Trans>
            If you don't have an account or haven't received an invitation,
          </Trans>{' '}
          <a href={ROUTES.SIGNUP} className="LinkNew">
            <Trans>apply for beta access</Trans>
          </a>
          .
        </p>
      </div>
    </div>
  )
}
